import { createPilotController } from './pilot-controller';
import { parseCommand } from '@atc/sim';
/**
 * Game-session implementation behind the public game facade.
 * One game session mounted into a root element: sim, radar scope, strips, radio log, command
 * bar and the tower view, arranged by the display layout. Wiring only; the sim owns the rules.
 * The tower view (three.js) is imported on first use so the menus stay light.
 */
import { clockText, metarLine, resolveConditions, startHours, toMagneticDeg, WEATHER, type AircraftState, type AirportData, type RealismLevel } from '@atc/core';
import { createCommandBar, createCommsLog, createRadarScope, createStrips } from '@atc/radar';
import { createRulesBrain, plannedPath, type RadioMessage, type Sim, type Stats } from '@atc/sim';
import { createLlmBrain, type LlmBrain } from './brain/llm-brain';
import { createCostMeter, loadPricing } from './costs';
import { describeSessionAi, type AiAllowance, type AiSummary, type SessionAiState } from './session-ai';
import { attribution } from './attribution';
import { createCoachPanel, type CoachPanel } from './academy/coach';
import { startLesson, type Lesson, type LessonRun } from './academy/lesson';
import { createLessonBriefPanel, lessonBrief } from './academy/brief';
import { startTutorialTour } from './academy/tutorial-tour';
import { createRadio } from './radio';
import { createVoicePanel } from './voice';
import { createAircraftCard } from './aircraft-card';
import { createConversationPartner } from './conversation';
import { atisBriefing, createAtisPanel } from './atis';
import { scenarioById, recordChallenge } from './scenarios';
import { createSessionSim, sessionWind, startChallenge } from './session';
import { AIRPORTS, airportEntry } from './airports';
import { isFullscreen, onFullscreenChange, toggleFullscreen } from './fullscreen';
import { commandHint } from './hints';
import { nextProcedureMode, procedureOverlay, routeAheadOverlay, runwayChip, type ProcedureMode } from './overlays';
import { browserStorage, type Layout, type SessionSettings } from './settings';
import { playAlert, unlockAudio, type AlertTone } from './sound';
import { createIdleWatch, type IdleReason } from './idle';
import { createTranscriptLedger } from './transcript';
import { buildSessionLog, createTrackRecorder } from './session-log';
import { GAME_TEMPLATE, VIEW_KEYS } from './game-shell';
import type { GameHooks, GameOptions, GameSession } from './game-contracts';
import { createGameTower } from './game-tower';
import { feedbackFor } from './game-feedback';
import { mountTowerOperations } from './tower-operations';

export type { GameHooks, GameOptions, GameSession } from './game-contracts';
export { VIEWS } from './game-shell';

export function startGame(root: HTMLElement, airport: AirportData, initial: SessionSettings, hooks: GameHooks, options: GameOptions = {}): GameSession {
  const settings = { ...initial };
  root.innerHTML = GAME_TEMPLATE;
  const $ = <T extends HTMLElement = HTMLElement>(sel: string) => root.querySelector(sel) as T;
  const gameEl = $('.game');
  $('.g-icao').textContent = airport.icao;
  $('.g-name').textContent = airportEntry(airport.icao)?.name ?? airport.name;
  document.title = `${airport.icao} — ATC`;

  const lesson = options.lesson;
  const tutorial = lesson?.id === 'tutorial';
  gameEl.classList.toggle('tutorial', tutorial);
  const challenge = !lesson && settings.mode === 'scenario';
  /** A lesson briefing holds the frequency; practice drills are one instruction and open straight on. */
  const briefHolds = !!lesson && lesson.briefing.objectives.length > 0 && !lesson.id.startsWith('practice-');
  const sim = createSessionSim(airport, settings, lesson);
  if (!lesson && !challenge) sim.timeScale = settings.position === 'tower' ? 1 : 0.5;
  let transcript = createTranscriptLedger(sim, { airport: airport.icao, seed: settings.seed, settings: settings as unknown as Record<string, unknown> });
  const initialRadio = sim.radioLog();
  for (const message of initialRadio) transcript.radio(message);
  const scope = createRadarScope($('.g-scope'), airport, { realism: sim.realism });
  // --- Conditions: lessons fly a clear day, since their checks and pictures assume one ---
  const conditionsWeather = () => (lesson ? 'clear' : settings.weather);
  /** Sim clock hour zero; the tower's clock runs forward from here with sim time. */
  let clockStartHour = startHours(airport, lesson ? 'day' : settings.timeOfDay);
  sim.setLocalClock(clockStartHour);
  const conditionsText = () => {
    const c = resolveConditions(airport, conditionsWeather(), clockStartHour, sim.simTimeS);
    const env = { ...c, windDirDeg: sim.env.windDirDeg ?? c.windDirDeg, windKt: sim.env.windKt ?? c.windKt };
    return { weather: `${clockText(c.timeOfDayHours)} · ${WEATHER[conditionsWeather()].label}`, metar: metarLine(airport.icao, env, WEATHER[conditionsWeather()].gusty) };
  };
  const disposeOperations = mountTowerOperations($('.tower-position'), sim, (callsign) => scope.setSelected(callsign));
  const strips = createStrips($('.g-strips'), airport);
  // Debug marks on the radio log (which brain spoke, and why the rules took over): ?debug, or Alt+D.
  let radioDebug = typeof location !== 'undefined' && new URLSearchParams(location.search).has('debug');
  const comms = createCommsLog($('.g-comms'), { debug: radioDebug });
  const tracks = createTrackRecorder(sim);
  const radio = createRadio({ enabled: settings.voice && !briefHolds, pace: settings.radioPace, provider: settings.radioProvider, noise: settings.radioNoise, accents: settings.radioAccents, onError: (text) => { $('.g-hint').textContent = text; } });
  let voicePanel: ReturnType<typeof createVoicePanel> | null = null;
  let transmitting = false;
  let preTransmitSpeed = sim.timeScale;
  // The controller already said microphone-originated transmissions aloud. Keep their ATC log
  // entry, but do not synthesize it back through the speakers; only the pilot response is played.
  /** A spoken clearance is marked in the sim so its playback can be skipped even when it airs later (tower queue). */
  const voiced = <T>(fn: () => T): T => { sim.setControllerVoiced(true); try { return fn(); } finally { sim.setControllerVoiced(false); } };
  const speedSel = $<HTMLSelectElement>('.g-speed');
  const pauseBtn = $('.g-pause'), sideBtn = $('.g-side-toggle'), fsBtn = $('.g-fullscreen'), towerPanel = $('.g-tower');
  const scoreEl = $('.g-score'), statusEl = $('.g-status'), realismEl = $('.g-realism');
  const mainEl = $('.g-main'), divider = $('.g-divider'), feedEl = $('.g-feed'), hintEl = $('.g-hint');
  let hintTarget: string | null = null, hintFrame = 0;
  let menuPaused = false, playerPaused = false, destroyed = false;
  let ended = false, warnedLastMinute = false;
  /** Academy: the sim waits after each passed step until the player continues. */
  let coachPaused = false;
  /** An opening panel (ATIS, challenge or lesson briefing) is still holding the frequency. */
  let awaitingStart = false;
  /**
   * Lesson setup places its traffic, and the pilots check in, before the briefing is up. Those calls
   * wait here for "Take the frequency": sent to the radio they would be cancelled before they were
   * heard, and Chrome's speech engine swallows the next utterance after such a cancel.
   */
  let heldRadio: RadioMessage[] | null = briefHolds ? [] : null;
  const hear = (m: RadioMessage) => { if (heldRadio) heldRadio.push(m); else radio.push(m); };
  let run: LessonRun | null = null, coach: CoachPanel | null = null;
  let tutorialTourActive = false, disposeTutorialTour: (() => void) | null = null;
  const checkLessonStep = () => {
    if (run?.status === 'running' && run.update() !== 'running') { coachPaused = true; syncPause(); coach?.render(run); }
  };
  /** Set while the lesson briefing is up, so destroying the session tears it down too. */
  let disposeBrief: (() => void) | null = null;
  const shiftEl = $('.g-shift');
  shiftEl.hidden = settings.mode !== 'shift';
  const tower = createGameTower({
    panel: towerPanel,
    container: $('.g-tower-view'),
    airport,
    sim,
    quality: settings.graphicsQuality,
    environment: () => {
      const c = resolveConditions(airport, conditionsWeather(), clockStartHour, sim.simTimeS);
      return { ...c, windDirDeg: sim.env.windDirDeg ?? c.windDirDeg, windKt: sim.env.windKt ?? c.windKt };
    },
    onSelect: (callsign) => scope.setSelected(callsign),
    onError: hooks.onError,
  });

  const trace = {
    start: (text: string, source: 'typed' | 'speech') => transcript.controller(text, source),
    finish: (id: string, result: { ok: boolean; status?: string; accepted?: unknown[]; rejected?: unknown[] }, details?: Record<string, unknown>) => {
      const unrecognized = details?.source === 'clarification' || details?.resolution === 'clarification' || details?.classification === 'unrecognized';
      transcript.outcome(id, result.ok ? 'accepted' : unrecognized ? 'unrecognized' : 'rejected', { ...details, ...(unrecognized ? { classification: 'unrecognized' } : {}), status: result.status, accepted: result.accepted, rejected: result.rejected });
    },
  };
  // Who you are talking to, so a follow-up without a callsign still reaches the right aircraft.
  const partner = createConversationPartner({
    onFrequency: (cs) => { const ac = sim.aircraft().find((a) => a.callsign === cs); return !!ac && !['landed', 'exited', 'handedOff', 'cancelled', 'forcedLanding'].includes(ac.phase ?? ''); },
  });
  const costs = createCostMeter();
  void loadPricing().then((table) => { if (table && !destroyed) costs.setPricing(table); });

  // --- What AI this session is really using, and what the player has left ---
  // Auto modes settle and fallbacks happen after start; these hold the settled facts so the header
  // chip says what is true now, not what setup asked for.
  let pilotsAi: SessionAiState['pilots'] = { mode: 'checking' };
  let languageAvailable: boolean | null = settings.languageProvider === 'auto' ? null : true;
  let languageRefused = false;
  let micAvailable: boolean | null = null, micConnected = false;
  let allowance: AiAllowance | null = null, toldBlocked: string | null = null;
  function aiState(): SessionAiState {
    const commands: SessionAiState['commands'] = settings.languageMode === 'offline' ? { mode: 'phrases', reason: languageAvailable === false ? 'unavailable' : 'setting' }
      : languageRefused ? { mode: 'phrases', reason: 'budget' }
      : languageAvailable === null ? { mode: 'checking' } : languageAvailable ? { mode: 'ai' } : { mode: 'phrases', reason: 'unavailable' };
    return {
      pilots: pilotsAi, commands,
      voices: !settings.voice ? 'off' : settings.radioProvider === 'openai' ? 'ai' : 'browser',
      mic: !settings.speechInput ? 'off' : micAvailable === false ? 'unavailable' : micConnected ? 'connected' : 'standby',
      allowance,
    };
  }
  const aiBtn = $<HTMLButtonElement>('.g-ai'), aiPanel = $('.g-ai-panel');
  const escHtml = (t: string) => t.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);
  function renderAi(): AiSummary {
    const summary = describeSessionAi(aiState());
    aiBtn.textContent = summary.chip.label;
    aiBtn.dataset.tone = summary.chip.tone;
    aiBtn.title = summary.chip.title;
    if (!aiPanel.hidden) {
      aiPanel.innerHTML = `<dl>${summary.rows.map((r) => `<dt>${escHtml(r.name)}</dt><dd${r.ai ? ' class="ai"' : ''}>${escHtml(r.value)}${r.why ? `<small>${escHtml(r.why)}</small>` : ''}</dd>`).join('')}</dl><p>Change these from the menu (Esc).</p>`;
    }
    const briefLine = mainEl.querySelector('[data-brief-ai]');
    if (briefLine) briefLine.textContent = summary.line;
    return summary;
  }
  const toggleAiPanel = (open: boolean) => { aiPanel.hidden = !open; aiBtn.setAttribute('aria-expanded', String(open)); renderAi(); };
  aiBtn.addEventListener('click', () => toggleAiPanel(aiPanel.hidden));
  const closeAiPanel = (e: PointerEvent) => { if (!aiPanel.hidden && !(e.target as Element | null)?.closest('.g-ai-wrap')) toggleAiPanel(false); };
  document.addEventListener('pointerdown', closeAiPanel);
  function refreshAllowance(): void {
    void fetch('/api/voice/allowance', { headers: { 'x-atc-voice': '1', ...attribution() } }).then(async (r) => {
      if (!r.ok || destroyed) return;
      allowance = await r.json() as AiAllowance;
      if (!allowance.blocked) { toldBlocked = null; if (pilotsAi.reason === 'budget' && llmBrain) pilotsAi = { mode: 'ai' }; }
      renderAi();
    }).catch(() => { /* the readout just keeps what it had */ });
  }
  refreshAllowance();
  // Spend moves slowly and the server is the only one who knows it; once a minute is plenty.
  const allowanceTimer = setInterval(() => { if (!destroyed && !sim.paused) refreshAllowance(); }, 60000);
  /** Sees every model call the session makes, so a refusal or a recovery shows at once. */
  const aiFetch: typeof fetch = async (input, init) => {
    const response = await fetch(input, init);
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    if (response.ok) {
      if (url.includes('/api/voice/brain') && llmBrain && pilotsAi.mode !== 'ai') { pilotsAi = { mode: 'ai' }; renderAi(); }
      if (url.includes('/api/voice/conversation') && languageRefused) { languageRefused = false; renderAi(); }
    } else if (response.status === 503) {
      const reason = await response.clone().json().then((b: { budget?: string }) => b.budget, () => undefined);
      if (reason && !destroyed) {
        allowance = { ...(allowance ?? { scope: 'daily' }), blocked: reason };
        if (url.includes('/api/voice/brain')) pilotsAi = { mode: 'rules', reason: 'budget' };
        if (url.includes('/api/voice/conversation')) languageRefused = true;
        const summary = renderAi();
        if (toldBlocked !== reason) { toldBlocked = reason; toast(`${summary.chip.title} Rules pilots and standard phrases keep working.`, 'warn'); }
        refreshAllowance();
      }
    }
    return response;
  };
  const pilotController = createPilotController(sim, settings, () => scope.getSelected(), aiFetch, partner, costs);
  if (settings.languageProvider === 'auto' && settings.languageMode !== 'offline') {
    const unavailable = () => { if (destroyed) return; languageAvailable = false; if (settings.languageMode === 'model') settings.languageMode = 'offline'; renderAi(); };
    void fetch('/api/voice/language').then(async (response) => {
      if (!response.ok) throw new Error();
      const caps = await response.json() as { hosted?: boolean; local?: boolean };
      if (!caps.hosted && !caps.local) unavailable(); else if (!destroyed) { languageAvailable = true; renderAi(); }
    }).catch(unavailable);
  }
  const bar = createCommandBar($('.g-command'), (line) => {
    pilotController.cancel();
    const shorthand = parseCommand(line);
    if (!shorthand.ok && !/^[A-Z0-9]{2,8}\s+(?:Q\s|STANDBY$|SAYAGAIN$|RC$|ACCEPT$|CG$|CA$|HP$|CROSS$)/i.test(line) && !/^FLOW\s/i.test(line)) {
      bar.setMessage('Interpreting…');
      void pilotController.send(line, 'typed', trace).then((r) => { if (r && !destroyed) bar.setMessage(r.message, r.ok); });
      return;
    }
    const traceId = transcript.controller(line, 'typed');
    const r = sim.command(line);
    transcript.outcome(traceId, r.ok ? 'accepted' : 'rejected', { status: r.status, accepted: r.accepted, rejected: r.rejected, system: r.system });
    bar.setMessage(r.message, r.ok);
    const sys = r.system;
    if (!sys) return;
    switch (sys.kind) {
      case 'scale': scope.toggleScale(); break;
      case 'stats': case 'help': bar.showText(r.message, r.text ?? ''); break;
      case 'quit': endSession('quit'); break;
      case 'airport':
        if (airportEntry(sys.icao)) hooks.onAirport(sys.icao.toLowerCase());
        else bar.setMessage(`No data for ${sys.icao}. Airports: ${AIRPORTS.map((a) => a.icao.toUpperCase()).join(', ')}`, false);
        break;
      case 'realism': applyRealism(sys.level); hooks.onSettingsChange({ realism: sys.level }); break;
      case 'timeScale': showSpeed(sys.factor); break;
      case 'pause': playerPaused = sim.paused; syncPause(); break;
      case 'view': setLayout(sys.layout); break;
      case 'fullscreen': void toggleFullscreen(); break;
      case 'brief': hooks.onBriefing?.(); break;
      case 'procedures': setProcedures(sys.mode ?? nextProcedureMode(settings.procedures)); break;
      case 'trail': setTrail(!settings.trail); break;
    }
  });
  // The aircraft card: click a blip or strip for what the pilot wants, what to say and keys that transmit.
  const card = createAircraftCard(mainEl, $('.g-scope'), sim, scope, {
    onTransmit: (command) => { pilotController.cancel(); partner.note(command.split(' ')[0]); const traceId = transcript.controller(command, 'typed'); const r = sim.command(command); transcript.outcome(traceId, r.ok ? 'accepted' : 'rejected', { source: 'aircraft-card', status: r.status, accepted: r.accepted, rejected: r.rejected }); if (r.ok) { run?.cardCommand(command); checkLessonStep(); } bar.setMessage(r.message, r.ok); if (!tutorial) bar.focus(); },
    onClose: () => scope.setSelected(null),
  });
  function applyAssists(on: boolean): void {
    card.setAssists(on);
    scope.setAssists(on);
    hintEl.hidden = !on;
  }
  applyAssists(settings.assists);
  bar.setMessage(`${sim.operations().position === 'tower' ? airport.radio.twr : airport.radio.app}. Type HELP for the command list. Click a blip or strip to open its card.`);

  function showSpeed(factor: number): void {
    if (![...speedSel.options].some((o) => o.value === String(factor))) speedSel.add(new Option(`×${factor}`, String(factor)));
    speedSel.value = String(factor);
  }
  showSpeed(sim.timeScale);
  /** Who the radio is pointed at, on the card of the aircraft picked on the radar. */
  function syncSpeechBanner(): void {
    card.setSpeech(!settings.speechInput || sim.paused || ended ? 'off' : transmitting ? 'live' : 'ready');
  }
  /**
   * A coach pause between lesson steps is a beat inside play, not a menu: a readback already on
   * the queue must still be heard, and disabling the radio aborts it. Only a menu, the player's
   * own pause, or an opening panel that has not been dismissed yet silences the frequency.
   */
  const frequencySilent = () => menuPaused || playerPaused || awaitingStart;
  function syncPause(): void {
    sim.paused = menuPaused || playerPaused || coachPaused;
    if (sim.paused) pilotController.cancel();
    radio.setEnabled(settings.voice && !frequencySilent() && !transmitting, transmitting && !frequencySilent());
    voicePanel?.setPaused(sim.paused || !settings.speechInput, frequencySilent() || ended || !settings.speechInput);
    $('.g-audio').textContent = settings.sound && (options.allowVoicePlayback === false || settings.voice) ? 'Mute audio' : 'Enable audio';
    card.setEnabled(!sim.paused && !ended);
    syncSpeechBanner();
    pauseBtn.classList.toggle('active', playerPaused);
    pauseBtn.textContent = playerPaused ? 'Resume' : 'Pause';
  }
  function endSession(reason: 'shift' | 'quit' | 'collision'): void {
    if (ended) return;
    ended = true;
    menuPaused = true;
    syncPause();
    bar.setEnabled(false);
    void transcript.finish();
    hooks.onEnd(sim.stats(), reason);
  }
  function applyRealism(level: RealismLevel): void {
    // A challenge is fixed at mid realism so its times compare; a lesson only starts at its own
    // level, and the player may change it from the pause menu or REALISM like anywhere else.
    if (challenge) return;
    settings.realism = level;
    if (sim.realism.level !== level) sim.setRealism(level);
    scope.setRealism(sim.realism);
    tower?.setQuality(settings.graphicsQuality);
    realismEl.textContent = `Realism ${level}`;
  }
  realismEl.textContent = challenge ? 'Challenge: mid, no wind, instant compliance, runway safety' : `Realism ${sim.realism.level}`;

  // --- Runways in use, procedure overlays, flight trail ---
  const rwyConfig = sim.runwayConfig();
  const chip = runwayChip(rwyConfig), chipEl = $('.g-runways'), procsBtn = $('.g-procs'), trailBtn = $('.g-trail');
  chipEl.textContent = chip.text; chipEl.title = chip.title; chipEl.classList.toggle('over', chip.over);
  chipEl.addEventListener('click', () => hooks.onBriefing?.());
  const PROC_LABEL: Record<ProcedureMode, string> = { active: 'Procs: in use', all: 'Procs: all', off: 'Procs: off' };
  function applyProcedures(mode: ProcedureMode): void {
    settings.procedures = mode;
    scope.setOverlay('procedures', procedureOverlay(airport, rwyConfig, mode));
    procsBtn.textContent = PROC_LABEL[mode];
    procsBtn.classList.toggle('active', mode !== 'off');
  }
  function setProcedures(mode: ProcedureMode): void { applyProcedures(mode); hooks.onSettingsChange({ procedures: mode }); }
  let routeShown = false;
  /** The selected aircraft's route ahead follows it every tick while the trail is on. */
  function syncRouteAhead(): void {
    const ac = settings.trail ? sim.find(scope.getSelected() ?? '') : undefined;
    const ov = ac ? routeAheadOverlay(plannedPath(ac, airport)) : null;
    if (ov || routeShown) scope.setOverlay('route', ov);
    routeShown = !!ov;
  }
  function applyTrail(on: boolean): void {
    settings.trail = on;
    scope.setTrail(on);
    trailBtn.classList.toggle('active', on);
    syncRouteAhead();
  }
  function setTrail(on: boolean): void { applyTrail(on); hooks.onSettingsChange({ trail: on }); }
  procsBtn.addEventListener('click', () => setProcedures(nextProcedureMode(settings.procedures)));
  trailBtn.addEventListener('click', () => setTrail(!settings.trail));
  applyProcedures(settings.procedures);
  applyTrail(settings.trail);

  // --- Who the pilots are, and what they cost ---
  let llmBrain: LlmBrain | null = null;
  let brainGeneration = 0;
  const startLlmBrain = (provider: SessionSettings['brainProvider']) => {
    llmBrain = createLlmBrain({
      sim, provider, meter: costs, capUsd: settings.brainSpendCapUsd, fetchFn: aiFetch,
      // A spent allowance already has its own toast and chip; the hint line is for the traffic.
      onError: (message) => { if (!allowance?.blocked) hintEl.textContent = message; },
      onCapReached: () => { pilotsAi = { mode: 'rules', reason: 'cap' }; renderAi(); toast('This session\'s AI pilot allowance is used up; scripted pilots are flying', 'warn'); },
    });
    sim.setBrain(llmBrain);
    pilotsAi = { mode: 'ai' };
    renderAi();
  };
  function applyBrain(mode: SessionSettings['pilotBrain']): void {
    const generation = ++brainGeneration;
    settings.pilotBrain = mode;
    llmBrain?.destroy();
    llmBrain = null;
    // Challenges are timed against a leaderboard, so their traffic always uses the rules brain.
    // Lessons take the configured brain like any other session: it only chooses words, the sim
    // still checks every value, and every lesson step is checked against flight state, not radio text.
    if (mode === 'rules' || challenge) { sim.setBrain(createRulesBrain()); pilotsAi = { mode: 'rules', reason: challenge ? 'challenge' : 'setting' }; renderAi(); return; }
    if (mode === 'llm' || settings.brainProvider !== 'auto') { startLlmBrain(settings.brainProvider); return; }
    sim.setBrain(createRulesBrain());
    pilotsAi = { mode: 'checking' }; renderAi();
    const rulesOnly = () => { if (!destroyed && generation === brainGeneration) { pilotsAi = { mode: 'rules', reason: 'unavailable' }; renderAi(); } };
    void fetch('/api/voice/brainInfo', { headers: { 'x-atc-voice': '1' } }).then(async (response) => {
      if (!response.ok) throw new Error();
      const caps = await response.json() as { hosted?: boolean; local?: boolean };
      if (!destroyed && generation === brainGeneration && (caps.hosted || caps.local)) startLlmBrain('auto');
      else rulesOnly();
    }).catch(rulesOnly); // automatic mode intentionally stays on the rules brain
  }
  applyBrain(settings.pilotBrain);

  // --- Selection sync: scope, strips, command box, tower view. ---
  scope.on('select', (cs) => { card.select(cs); strips.setSelected(cs); if (cs) { hintTarget = cs; updateHint(sim.aircraft()); bar.prefill(cs); tower?.setCamera({ trackCallsign: cs }); if (!tutorialTourActive) { run?.select(cs); checkLessonStep(); } } });
  strips.on('select', (cs) => scope.setSelected(cs));

  // --- Sim events → UI ---
  // Initial aircraft check in while the sim is being constructed, before this listener exists.
  // Hand that backlog to both displays and audio once; the radio serializes it without overlap.
  for (const m of initialRadio) { comms.push(m); hear(m); }
  sim.on('radio', (m) => { if (m.from === 'pilot' && m.callsign) partner.note(m.callsign); transcript.radio(m); comms.push(m); if (!(m.voiced && !settings.echoSpeech)) hear(m); });
  sim.on('message', (t) => {
    // Which brain spoke is a developer's question: the radio log marks it under debug (?debug, Alt+D),
    // and a player should not read a model refusal as something they did wrong.
    if (/: pilot brain fell back to the rules/.test(t) && !radioDebug) return;
    bar.setMessage(t, !/^SEPARATION|unable|no such|unknown|not a|needs/i.test(t));
  });
  sim.on('tick', (states, t) => {
    scope.update(states, t);
    strips.update(states);
    card.update(states);
    syncRouteAhead();
    const st = sim.stats();
    scoreEl.textContent = `Score ${st.score}`;
    const w = sim.realism.showWindInHeader ? `  wind ${String(Math.round(toMagneticDeg(airport, sim.env.windDirDeg ?? 0)) || 360).padStart(3, '0')}/${Math.round(sim.env.windKt ?? 0)}` : '';
    statusEl.textContent = `${fmtTime(t)}  ${states.length} ac  L${st.landings} H${st.handoffs} C${st.conflicts} W${states.filter((s) => s.waiting).length}${w}`;
    if (++hintFrame % 15 === 0) updateHint(states);
    // Shift clock runs on sim time, so SPEED shortens the wait without changing the shift.
    if (settings.mode === 'shift' && !ended) {
      const left = settings.shiftMinutes * 60 - t;
      shiftEl.textContent = `Shift ${fmtClock(Math.max(0, left))}`;
      shiftEl.classList.toggle('ending', left <= 60);
      if (left <= 60 && !warnedLastMinute) { warnedLastMinute = true; toast('One minute left in the shift', 'warn'); }
      if (left <= 0) endSession('shift');
    }
    checkLessonStep();
  });

  // --- Feedback: score feed, alert tones, command hint ---
  function toast(text: string, tone: 'good' | 'bad' | 'warn'): void {
    const el = document.createElement('div');
    el.className = `g-toast ${tone}`;
    el.textContent = text;
    feedEl.prepend(el);
    while (feedEl.children.length > 4) feedEl.lastElementChild?.remove();
    setTimeout(() => el.remove(), 5000);
  }
  const alert = (t: AlertTone) => { if (settings.sound) playAlert(t); };
  sim.on('event', (ev) => {
    if (ev.type === 'collision' && !ended) {
      coachPaused = true;
      syncPause();
      bar.setEnabled(false);
      tower?.tick(1);
      tower?.showImpact(ev);
      tower?.setCamera({ position: { lat: ev.lat, lon: ev.lon, altitudeM: ev.altitudeFt * 0.3048 + 90 }, yawDeg: 0, pitchDeg: -28, trackCallsign: null });
      const notice = document.createElement('div');
      notice.className = 'g-impact-notice';
      notice.setAttribute('role', 'alert');
      notice.innerHTML = `<strong>Aircraft collision</strong><span>${ev.a.callsign} / ${ev.b.callsign}</span><small>Incident review opening…</small>`;
      gameEl.append(notice);
      setTimeout(() => {
        notice.remove();
        if (destroyed) return;
        if (lesson) checkLessonStep();
        else endSession('collision');
      }, 2400);
    }
    const feedback = feedbackFor(ev);
    if (!feedback) return;
    toast(feedback.text, feedback.tone);
    if (feedback.alert) alert(feedback.alert);
  });
  function updateHint(states: readonly AircraftState[]): void {
    const s = hintTarget ? states.find((x) => x.callsign === hintTarget) : undefined;
    hintEl.textContent = s?.control ? `${s.callsign}: ${s.control.offeredTo === 'tower' ? 'Accept the transfer in the Tower position panel.' : s.control.owner !== 'tower' ? `With ${s.control.owner}.` : s.control.offeredTo ? `Ready to contact ${s.control.offeredTo}.` : s.onGround ? 'Check the runway before line-up or takeoff.' : 'Monitor final and issue landing clearance when appropriate.'}` : s ? `${s.callsign}: ${commandHint(s, airport, { approachRole: sim.approachRole })}` : 'Click an aircraft or type its callsign for a suggested command.';
  }
  updateHint([]);

  // Push to talk is a game setting, but an exercise is where a player first wants it, so the
  // lesson briefing and the coach can switch it on without a trip back to setup.
  const allowSpeech = options.allowSpeechInput !== false;
  function setSpeechInput(on: boolean): void {
    if (settings.speechInput === on || (on && !allowSpeech)) return;
    settings.speechInput = on;
    mountVoice();
    hooks.onSettingsChange({ speechInput: on });
  }
  const speechControl = { allowed: allowSpeech, enabled: () => settings.speechInput, onChange: setSpeechInput };
  const allowPlayback = options.allowVoicePlayback !== false;
  function setVoicePlayback(on: boolean): void {
    if (settings.voice === on || (on && !allowPlayback)) return;
    settings.voice = on;
    syncPause();
    hooks.onSettingsChange({ voice: on });
  }

  function mountVoice(): void {
    voicePanel?.destroy(); voicePanel = null; micConnected = false;
    $('.g-voice').hidden = !settings.speechInput;
    if (settings.speechInput) voicePanel = createVoicePanel($('.g-voice'), sim, {
      onSession: (id) => { micConnected = !!id; renderAi(); },
      onAvailability: (available) => { micAvailable = available; renderAi(); },
      interpretation: settings.voiceInterpretation,
      sendText: async (text) => {
        sim.setControllerVoiced(true);
        try { return await pilotController.send(text, 'speech', trace); }
        finally { sim.setControllerVoiced(false); }
      },
      onTranscript: (text) => transcript.controller(text, 'speech'),
      onDirectResult: (id, result, details) => trace.finish(id, result, details),
      sendTransmission: (tx) => { pilotController.cancel(); return voiced(() => sim.transmission(tx)); },
      onTransmit: (active) => { if (active) pilotController.cancel(); transmitting = active; if (active) { preTransmitSpeed = sim.timeScale; sim.timeScale = 0.5; showSpeed(0.5); radio.interrupt(); } else { sim.timeScale = preTransmitSpeed; showSpeed(preTransmitSpeed); } radio.setEnabled(settings.voice && !active && !frequencySilent(), active && !frequencySilent()); syncSpeechBanner(); speedSel.disabled = active; },
      send: (proposal) => { pilotController.cancel(); if (sim.paused || ended) return { ok: false, message: 'Resume the session before transmitting.' }; return voiced(() => { const result = sim.clearance(proposal); bar.setMessage(result.message, result.ok); return result; }); },
      sayAgain: (callsign) => { if (!sim.paused && !ended) sim.sayAgain(callsign); },
      partner: () => (settings.addressing === 'conversation' ? partner.current() : settings.addressing === 'selected' ? scope.getSelected() : null),
      // Picking a blip is a deliberate "this one": it biases transcription and stands in for a
      // callsign the transcript loses, unless the player asked for explicit addressing.
      selected: () => (settings.addressing === 'explicit' ? null : scope.getSelected()),
    });
    syncPause();
  }
  mountVoice();
  $('.g-audio').addEventListener('click', () => {
    const enabled = !settings.sound || (allowPlayback && !settings.voice);
    settings.sound = enabled; settings.voice = enabled && allowPlayback;
    if (!enabled) settings.speechInput = false;
    if (!enabled) { radio.interrupt(); voicePanel?.setPaused(true); }
    syncPause(); hooks.onSettingsChange({ sound: settings.sound, voice: settings.voice, speechInput: settings.speechInput });
  });

  // --- Repeatable challenge briefing and objective ---
  let challengeRun: ReturnType<typeof startChallenge> | null = null;
  if (challenge) {
    challengeRun = startChallenge(sim, settings.scenario);
    coachPaused = awaitingStart = true;
    const spec = scenarioById(settings.scenario), panel = $('.g-challenge');
    panel.hidden = false;
    const title = document.createElement('h2'); title.textContent = spec.title;
    const text = document.createElement('p'); text.textContent = spec.briefing;
    const action = document.createElement('button'); action.className = 'g-btn'; action.textContent = 'Start challenge';
    panel.append(title, text, action);
    action.addEventListener('click', () => { if (action.dataset.done) { hooks.onRestart?.(); return; } coachPaused = awaitingStart = false; syncPause(); action.hidden = true; });
    sim.on('tick', () => {
      if (coachPaused) return;
      const progress = challengeRun!.update();
      text.textContent = progress.text;
      if (progress.status !== 'running') { coachPaused = true; syncPause(); action.hidden = false; action.dataset.done = '1'; action.textContent = 'Try the same traffic again'; if (progress.status === 'complete') recordChallenge(browserStorage(), airport.icao, settings.scenario, sim.simTimeS, settings.position, settings.coaching !== false); }
    });
    syncPause();
  }

  // --- Opening brief: the frequency stays silent until the player takes it ---
  // The sim holds the opening check-ins behind a short delay and does not step while paused, so
  // nobody calls until the panel is dismissed; the two are not otherwise coupled.
  if (!lesson && !challenge && settings.atis) {
    coachPaused = awaitingStart = true;
    const brief = atisBriefing(airport, sim.runwayConfig(), { traffic: sim.aircraft().length, name: airportEntry(airport.icao)?.name, tower: sim.operations().position === 'tower', wind: { directionDeg: sim.env.windDirDeg ?? airport.wind.directionDeg, speedKt: sim.env.windKt ?? airport.wind.speedKt }, conditions: conditionsText() });
    createAtisPanel($('.g-challenge'), brief, {
      onStart: () => { coachPaused = awaitingStart = false; syncPause(); bar.focus(); },
      onBriefing: () => hooks.onBriefing?.(),
      onSuppress: (skip) => { settings.atis = !skip; hooks.onSettingsChange({ atis: !skip }); },
    });
    syncPause();
  }

  // --- Academy lesson ---
  if (lesson) {
    const r = startLesson(lesson, sim, airport);
    run = r;
    const panel = createCoachPanel(mainEl, lesson, {
      nextTitle: options.nextLessonTitle,
      speech: speechControl,
      onContinue: () => {
        r.next();
        if (tutorial && r.stepIndex === 4) scope.setSelected(null);
        panel.render(r);
        if (r.status === 'complete') { hooks.onLessonComplete?.(lesson.id, panel.assist()); return; }
        coachPaused = !!tutorial && (r.stepIndex === 0 || r.stepIndex === 4);
        syncPause();
        if (!tutorial) bar.focus();
      },
      // Steps that need more than one call prefill the first and leave the rest on screen.
      onShowMe: (commands) => bar.prefill(commands[0]!),
      onRetry: () => hooks.onRestart?.(),
      onExit: () => hooks.onLessonExit?.(),
      onNext: () => hooks.onLessonNext?.(),
      ...(tutorial ? { onWatch: () => hooks.onTutorialWatch?.() } : {}),
      ...(options.offerShift ? { onShift: () => hooks.onFirstShift?.() } : {}),
    });
    coach = panel;
    panel.render(r);
    // The briefing holds the frequency: the sim does not step behind it, so the picture the player
    // reads about is the picture they get. Practice drills are one instruction and open straight on.
    if (briefHolds) {
      coachPaused = awaitingStart = true;
      bar.setEnabled(false);
      disposeBrief = createLessonBriefPanel(mainEl, lessonBrief(lesson, airport), {
        speech: { ...speechControl, enabled: settings.speechInput },
        playback: { allowed: allowPlayback, enabled: settings.voice, onChange: setVoicePlayback },
        onStart: () => {
          disposeBrief = null;
          const openFrequency = () => {
            awaitingStart = false; coachPaused = !!tutorial; syncPause();
            const held = heldRadio ?? []; heldRadio = null;
            for (const m of held) radio.push(m);
          };
          bar.setEnabled(true); if (!tutorial) bar.focus();
          // The screen tour still holds the frequency: the opening calls air once the player has looked around.
          if (tutorial) {
            tutorialTourActive = true;
            disposeTutorialTour = startTutorialTour(root, (callsign) => scope.setSelected(callsign), () => { tutorialTourActive = false; disposeTutorialTour = null; if (!destroyed) openFrequency(); });
          } else openFrequency();
        },
        onExit: () => { disposeBrief = null; hooks.onLessonExit?.(); },
      });
      renderAi();
      syncPause();
    }
  }
  // --- Auto-pause: nobody on frequency, or the tab went to the background ---
  let idlePauses = 0, idleNotice: HTMLElement | null = null;
  const resumeFromIdle = (e?: Event) => {
    if (!idleNotice) return;
    e?.preventDefault(); e?.stopPropagation();
    idleNotice.remove(); idleNotice = null;
    document.removeEventListener('keydown', resumeFromIdle, true);
    playerPaused = false; syncPause(); bar.focus();
    hooks.onIdlePause?.('idle', false);
  };
  const idleWatch = createIdleWatch({
    minutes: options.idlePauseMinutes ?? 3,
    onIdle(reason: IdleReason) {
      // Only a session that is running: a menu, a briefing, a lesson beat or the player's own
      // pause already stops the sim, and an ended shift has nothing left to spend.
      if (destroyed || ended || sim.paused || awaitingStart || disposeBrief || idleNotice) return;
      idlePauses++;
      playerPaused = true; syncPause();
      idleNotice = document.createElement('div');
      idleNotice.className = 'idle-notice'; idleNotice.setAttribute('role', 'alertdialog'); idleNotice.setAttribute('aria-live', 'assertive');
      idleNotice.innerHTML = `<div class="idle-strip"><span class="idle-stamp">HOLD</span>
        <b>Still on frequency?</b>
        <small>${reason === 'hidden' ? 'Traffic held while this tab was in the background.' : 'Traffic held after a few quiet minutes, so nothing runs up while you are away.'}</small>
        <button type="button" class="g-btn lit">Resume</button><small class="idle-key">or press any key</small></div>`;
      idleNotice.querySelector('button')!.addEventListener('click', resumeFromIdle);
      mainEl.append(idleNotice);
      document.addEventListener('keydown', resumeFromIdle, true);
      hooks.onIdlePause?.(reason, true);
    },
  });
  const unlock = () => unlockAudio();
  document.addEventListener('pointerdown', unlock, { once: true });
  document.addEventListener('keydown', unlock, { once: true });

  // --- Header controls ---
  speedSel.addEventListener('change', () => { sim.timeScale = Number(speedSel.value); });
  pauseBtn.addEventListener('click', () => { if (idleNotice) { resumeFromIdle(); return; } playerPaused = !playerPaused; syncPause(); });
  $('.g-menu').addEventListener('click', () => hooks.onMenu());
  $('.g-views').addEventListener('click', (e) => { const v = (e.target as HTMLElement).closest<HTMLElement>('[data-view]')?.dataset.view; if (v) setLayout(v as Layout); });
  $('.g-tower-close').addEventListener('click', () => setLayout('radar'));
  sideBtn.addEventListener('click', () => setSide(!settings.side));
  fsBtn.addEventListener('click', () => void toggleFullscreen());
  $('.g-brief').addEventListener('click', () => hooks.onBriefing?.());
  const syncFullscreen = () => { fsBtn.textContent = isFullscreen() ? 'Exit full screen' : 'Full screen'; fsBtn.classList.toggle('active', isFullscreen()); };
  const offFullscreen = onFullscreenChange(syncFullscreen);
  syncFullscreen();

  // --- Layout ---
  function applyLayout(layout: Layout): void {
    settings.layout = layout;
    gameEl.dataset.layout = layout;
    for (const b of root.querySelectorAll<HTMLElement>('[data-view]')) b.classList.toggle('active', b.dataset.view === layout);
    tower.setVisible(layout !== 'radar');
  }
  function setLayout(layout: Layout): void { applyLayout(layout); hooks.onSettingsChange({ layout }); }
  function applySide(shown: boolean): void {
    settings.side = shown;
    gameEl.dataset.side = shown ? 'shown' : 'hidden';
    sideBtn.classList.toggle('active', shown);
  }
  function setSide(shown: boolean): void { applySide(shown); hooks.onSettingsChange({ side: shown }); }
  function applySplit(ratio: number): void {
    settings.split = ratio;
    gameEl.style.setProperty('--split-a', `${(ratio * 100).toFixed(2)}fr`);
    gameEl.style.setProperty('--split-b', `${((1 - ratio) * 100).toFixed(2)}fr`);
  }
  divider.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    divider.setPointerCapture(e.pointerId);
    divider.classList.add('dragging');
    const move = (ev: PointerEvent) => {
      const left = mainEl.getBoundingClientRect().left;
      const side = settings.side ? $('.g-side').offsetWidth : 0;
      const usable = mainEl.clientWidth - side - divider.offsetWidth;
      applySplit(Math.min(0.8, Math.max(0.2, (ev.clientX - left) / usable)));
    };
    const up = () => {
      divider.classList.remove('dragging');
      divider.removeEventListener('pointermove', move);
      divider.removeEventListener('pointerup', up);
      divider.removeEventListener('pointercancel', up);
      hooks.onSettingsChange({ split: settings.split });
    };
    divider.addEventListener('pointermove', move);
    divider.addEventListener('pointerup', up);
    divider.addEventListener('pointercancel', up);
  });

  // --- Keys: Alt+1-4 layouts, Alt+F full screen, Alt+S side panel, Alt+P procedures, Alt+T trail; Esc opens the menu once the box is empty. ---
  const input = root.querySelector<HTMLInputElement>('.rs-input')!;
  const textPanel = root.querySelector<HTMLElement>('.rs-textpanel')!;
  // Typing a callsign shows its hint before anything is selected.
  input.addEventListener('input', () => {
    const cs = input.value.trim().split(/\s+/)[0]?.toUpperCase();
    if (cs && sim.find(cs)) { hintTarget = cs; updateHint(sim.aircraft()); }
  });
  const onKey = (e: KeyboardEvent) => {
    if (menuPaused || disposeBrief) return;
    if (e.altKey && !e.ctrlKey && !e.metaKey) {
      const view = VIEW_KEYS[e.code];
      if (view) { e.preventDefault(); setLayout(view); return; }
      if (e.code === 'KeyF') { e.preventDefault(); void toggleFullscreen(); return; }
      if (e.code === 'KeyS') { e.preventDefault(); setSide(!settings.side); return; }
      if (e.code === 'KeyP') { e.preventDefault(); setProcedures(nextProcedureMode(settings.procedures)); return; }
      if (e.code === 'KeyT') { e.preventDefault(); setTrail(!settings.trail); return; }
      if (e.code === 'KeyD') { e.preventDefault(); radioDebug = !radioDebug; $('.g-comms').classList.toggle('rs-debug', radioDebug); toast(`Radio debug marks ${radioDebug ? 'on' : 'off'}`, 'good'); return; }
    }
    if (e.key !== 'Escape') return;
    if (!textPanel.hidden) { bar.hideText(); e.preventDefault(); return; }
    if (input.value !== '') return; // the command bar clears the box first
    if (card.isOpen()) { e.preventDefault(); e.stopPropagation(); scope.setSelected(null); return; }
    // Stop here: the menu adds its own Esc listener during this dispatch and would close at once.
    e.preventDefault();
    e.stopPropagation();
    hooks.onMenu();
  };
  document.addEventListener('keydown', onKey, true);

  // --- Loop ---
  let raf = 0, last = performance.now(), usageAcc = 0, spokenCharacters = 0;
  const loop = () => {
    const now = performance.now();
    const dt = Math.min(0.25, (now - last) / 1000);
    last = now;
    sim.step(dt);
    tracks.sample();
    usageAcc += dt;
    if (usageAcc >= 1) {
      usageAcc = 0;
      const usage = radio.usage();
      if (usage.speechCharacters > spokenCharacters) { costs.recordSpeech(usage.speechCharacters - spokenCharacters); spokenCharacters = usage.speechCharacters; }
      renderAi();
    }
    tower.tick(dt);
    raf = requestAnimationFrame(loop);
  };
  raf = requestAnimationFrame(loop);
  applySplit(settings.split);
  applySide(settings.side);
  applyLayout(settings.layout);
  if (!disposeBrief) bar.focus();
  (window as any).atc = { sim, scope, airport, get tower() { return tower.view; } };

  return {
    sim,
    practiceAt(timeS) {
      if (lesson || challenge || !sim.practiceAt(timeS)) return false;
      pilotController.cancel(); partner.clear(); radio.interrupt();
      transcript = createTranscriptLedger(sim, { airport: airport.icao, seed: settings.seed, settings: { ...settings, practice: true, checkpointS: sim.simTimeS } });
      ended = false; menuPaused = false; playerPaused = false; warnedLastMinute = false;
      bar.setEnabled(true); syncPause();
      bar.setMessage('Practice resumed from checkpoint. This attempt is not ranked.');
      return true;
    },
    airport,
    setHighlight(lines) { scope.setHighlight(lines); },
    openLessonBriefing(onClose) {
      if (!lesson) return false;
      if (disposeBrief) return true;
      disposeBrief = createLessonBriefPanel(mainEl, lessonBrief(lesson, airport), {
        mode: 'review',
        onStart: () => { disposeBrief = null; onClose(); },
      });
      return true;
    },
    costs() { return costs.summary(sim.simTimeS); },
    ai() { return renderAi(); },
    transcript() { return transcript.snapshot(); },
    sessionLog() {
      return buildSessionLog({
        sim, transcript: transcript.snapshot(), tracks: tracks.tracks(),
        ...(lesson ? { lesson: { id: lesson.id, ...(run?.step ? { step: run.step.title } : {}), ...(run ? { status: run.status } : {}) } } : {}),
      });
    },
    idlePauses() { return idlePauses; },
    pause() { menuPaused = true; syncPause(); bar.setEnabled(false); if (!aiPanel.hidden) toggleAiPanel(false); },
    resume() { if (ended) return; menuPaused = false; syncPause(); bar.setEnabled(true); bar.focus(); },
    end() { endSession('quit'); },
    apply(patch) {
      if (patch.realism) applyRealism(patch.realism);
      if (patch.timeOfDay !== undefined && !lesson) { settings.timeOfDay = patch.timeOfDay; clockStartHour = startHours(airport, patch.timeOfDay) - sim.simTimeS / 3600; sim.setLocalClock(clockStartHour); }
      if (patch.weather !== undefined && !lesson) {
        settings.weather = patch.weather;
        // A challenge flies without wind so its times compare; only the sky changes.
        if (!challenge) { const w = sessionWind(airport, settings); sim.setWind(w.wind, w.gustyWind); }
      }
      if (patch.radioPace !== undefined) { settings.radioPace = patch.radioPace; radio.setPace(patch.radioPace); }
      if (patch.radioNoise !== undefined) { settings.radioNoise = patch.radioNoise; radio.setNoise(patch.radioNoise); }
      if (patch.radioAccents !== undefined) { settings.radioAccents = patch.radioAccents; radio.setAccents(patch.radioAccents); }
      if (patch.languageMode !== undefined || patch.languageProvider !== undefined || patch.addressing !== undefined || patch.pilotInitiative !== undefined || patch.shortCallsigns !== undefined || patch.languageBudget !== undefined || patch.voiceInterpretation !== undefined) {
        pilotController.cancel(); Object.assign(settings, patch); sim.configurePilots({ initiative: settings.pilotInitiative, shortCallsigns: settings.shortCallsigns });
        if (patch.voiceInterpretation !== undefined) mountVoice();
      }
      if (patch.voice !== undefined) { settings.voice = patch.voice; syncPause(); }
      if (patch.radioProvider !== undefined) { settings.radioProvider = patch.radioProvider; radio.setProvider(patch.radioProvider); mountVoice(); }
      if (patch.speechInput !== undefined) { settings.speechInput = patch.speechInput; mountVoice(); }
      if (patch.echoSpeech !== undefined) settings.echoSpeech = patch.echoSpeech;
      if (patch.voiceImmediate !== undefined) settings.voiceImmediate = patch.voiceImmediate; // retired: pilots read back instead
      if (patch.layout) applyLayout(patch.layout);
      if (patch.side !== undefined) applySide(patch.side);
      if (patch.split !== undefined) applySplit(patch.split);
      if (patch.sound !== undefined) { settings.sound = patch.sound; syncPause(); }
      if (patch.graphicsQuality !== undefined) { settings.graphicsQuality = patch.graphicsQuality; tower.setQuality(patch.graphicsQuality); }
      if (patch.assists !== undefined) { settings.assists = patch.assists; applyAssists(patch.assists); }
      if (patch.procedures !== undefined) applyProcedures(patch.procedures);
      if (patch.trail !== undefined) applyTrail(patch.trail);
      if (patch.brainProvider !== undefined) settings.brainProvider = patch.brainProvider;
      if (patch.brainSpendCapUsd !== undefined) settings.brainSpendCapUsd = patch.brainSpendCapUsd;
      if (patch.pilotBrain !== undefined || patch.brainProvider !== undefined || patch.brainSpendCapUsd !== undefined) applyBrain(patch.pilotBrain ?? settings.pilotBrain);
    },
    destroy() {
      disposeOperations();
      pilotController.cancel();
      if (destroyed) return;
      destroyed = true;
      disposeTutorialTour?.();
      cancelAnimationFrame(raf);
      idleWatch.dispose(); document.removeEventListener('keydown', resumeFromIdle, true); idleNotice?.remove();
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('pointerdown', unlock);
      document.removeEventListener('pointerdown', closeAiPanel);
      clearInterval(allowanceTimer);
      document.removeEventListener('keydown', unlock);
      offFullscreen();
      tower.destroy();
      run?.dispose();
      challengeRun?.dispose();
      disposeBrief?.();
    coach?.destroy();
      llmBrain?.destroy();
      voicePanel?.destroy(); radio.destroy();
      card.destroy();
      scope.destroy(); strips.destroy(); comms.destroy(); bar.destroy();
      root.replaceChildren();
      delete (window as any).atc;
    },
  };
}

const fmtClock = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
const fmtTime = (t: number) => `${String(Math.floor(t / 3600)).padStart(2, '0')}:${String(Math.floor(t / 60) % 60).padStart(2, '0')}:${String(Math.floor(t % 60)).padStart(2, '0')}`;
